import { useEffect } from 'react';
import { createRouter, createRootRoute, createRoute, Outlet, useMatches, redirect } from '@tanstack/react-router';
import PublicProfile from 'app/javascript/pages/PublicProfile';
import ProfilesList from 'app/javascript/pages/ProfilesList';
import CompaniesList from 'app/javascript/pages/CompaniesList';
import CompanyPublicPage from 'app/javascript/pages/CompanyPublicPage';
import CompanyStorePage from 'app/javascript/pages/CompanyStorePage';
import CompanyStoreProductPage from 'app/javascript/pages/CompanyStoreProductPage';
import CompanyFridgePage from 'app/javascript/pages/CompanyFridgePage';
import FridgePosterPage, { FridgeQrPrintPage } from 'app/javascript/pages/FridgePosterPage';
import CompaniesLandingPage from 'app/javascript/pages/CompaniesLandingPage';
import FaceRecognitionLandingPage from 'app/javascript/pages/FaceRecognitionLandingPage';
import MovedToAppPage from 'app/javascript/pages/MovedToAppPage';
import CompanyQrPrint from 'app/javascript/pages/settings/CompanyQrPrint';
import JoinAppLanding from 'app/javascript/pages/JoinAppLanding';
import AdminPage from 'app/javascript/pages/admin/AdminPage';
import AdminCompanyPage from 'app/javascript/pages/admin/AdminCompanyPage';
import AdminLogin from 'app/javascript/pages/admin/AdminLogin';
import ProtectedRoute from 'app/javascript/components/ProtectedRoute';
import PageLayout from 'app/javascript/layouts/PageLayout';
import Navbar from 'app/javascript/components/shared/Navbar';
import { ROUTES } from 'app/javascript/constants/routes';

const rootRoute = createRootRoute({
  component: () => {
    // Immersive routes (declared via `staticData: { immersive: true }`) render
    // without the global chrome, such as the printable QR posters.
    const isImmersive = useMatches().some((match) => match.staticData?.immersive);

    return (
      <>
        {!isImmersive && (
          <div className="sticky top-0 z-40">
            <Navbar />
          </div>
        )}
        <Outlet />
      </>
    );
  },
});

const profilesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.PROFILES,
  component: ProfilesList,
});

// Owner-only debug dashboard — no nav entry, reached by direct URL. AdminPage
// gates rendering on the /me app_admin flag; the API enforces the real 403.
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.ADMIN,
  component: () => (
    <ProtectedRoute>
      <PageLayout>
        <AdminPage />
      </PageLayout>
    </ProtectedRoute>
  ),
});

// Per-company health page reached from the admin companies list.
const adminCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.ADMIN_COMPANY,
  component: () => (
    <ProtectedRoute>
      <PageLayout>
        <AdminCompanyPage />
      </PageLayout>
    </ProtectedRoute>
  ),
});

// Profile settings, dependents, academy management and face enrollment all
// moved to the native app. `/app` is the explanation page; the splat catches
// every legacy `/app/*` URL still circulating in e-mails and bookmarks.
const movedToAppRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.APP_ONLY,
  component: MovedToAppPage,
});

const movedToAppLegacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.APP_ONLY_LEGACY,
  component: MovedToAppPage,
});

// The web wizard is gone: academies are created in the app.
const createCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.COMPANY_NEW,
  beforeLoad: () => {
    throw redirect({ to: ROUTES.APP_ONLY });
  },
});

// The legacy `.pdf` join URL and the canonical native invitation share the
// first route because TanStack treats the final segment as one parameter. The
// pathname suffix is the unambiguous discriminator between poster and app CTA.
const companyOnboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.ONBOARDING_QR,
  staticData: { immersive: true },
  component: () => window.location.pathname.endsWith('.pdf') ? <CompanyQrPrint /> : <JoinAppLanding />,
});

// New print URL lives outside `/join/*`, which is reserved for native app links.
const companyQrPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.ONBOARDING_QR_PRINT,
  staticData: { immersive: true },
  component: CompanyQrPrint,
});

// Company Routes
const companiesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.COMPANIES,
  component: CompaniesList,
});

const companiesLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.COMPANY_LANDING,
  component: CompaniesLandingPage,
});

const faceRecognitionLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.FACE_RECOGNITION_LANDING,
  component: FaceRecognitionLandingPage,
});

// Home, guides and legal documents are rendered by Rails (see config/routes.rb); an
// in-app navigation to one of them reloads the page so Rails serves it.
const ServerRenderedPage = () => {
  useEffect(() => {
    window.location.reload();
  }, []);

  return <main className="min-h-screen bg-slate-950" aria-label="Carregando a página" />;
};

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.HOME,
  component: ServerRenderedPage,
});

const faqIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.FAQ_INDEX,
  component: ServerRenderedPage,
});

const faqPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.FAQ_PAGE,
  component: ServerRenderedPage,
});

const guideIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.GUIDE_INDEX,
  component: ServerRenderedPage,
});

const guidePageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.GUIDE_PAGE,
  component: ServerRenderedPage,
});

const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.PRIVACY_POLICY,
  component: ServerRenderedPage,
});

const privacyPolicyEnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.PRIVACY_POLICY_EN,
  component: ServerRenderedPage,
});

const termsOfUseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.TERMS_OF_USE,
  component: ServerRenderedPage,
});

const termsOfUseEnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.TERMS_OF_USE_EN,
  component: ServerRenderedPage,
});

const companyPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.COMPANY_DETAILS,
  component: CompanyPublicPage,
});

const companyStoreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.COMPANY_STORE,
  component: CompanyStorePage,
});

const companyStoreProductRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.COMPANY_STORE_PRODUCT,
  component: CompanyStoreProductPage,
});

const companyFridgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.COMPANY_FRIDGE,
  staticData: { immersive: true },
  component: CompanyFridgePage,
});

// Printable fridge poster: immersive so the browser prints only the sheet.
const fridgePosterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.FRIDGE_POSTER,
  staticData: { immersive: true },
  component: FridgePosterPage,
});

// QR-only print sheet: no product menu, sized as a reusable fridge label.
const fridgeQrPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.FRIDGE_QR_PRINT,
  staticData: { immersive: true },
  component: FridgeQrPrintPage,
});

// Public Routes
const publicProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.PUBLIC_PROFILE,
  component: PublicProfile,
});

// Sign-in lives in the native app. The web keeps one door, for the app owner
// into the admin dashboard; the legacy auth URLs explain that instead.
const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.ADMIN_LOGIN,
  component: AdminLogin,
});

const legacyLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.LEGACY_LOGIN,
  beforeLoad: () => {
    throw redirect({ to: ROUTES.APP_ONLY });
  },
});

const legacyFinishSignUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.LEGACY_FINISH_SIGNUP,
  beforeLoad: () => {
    throw redirect({ to: ROUTES.APP_ONLY });
  },
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  profilesListRoute,
  adminRoute,
  adminCompanyRoute,
  movedToAppRoute,
  movedToAppLegacyRoute,
  companyOnboardingRoute,
  companyQrPrintRoute,
  companiesListRoute,
  companiesLandingRoute,
  faceRecognitionLandingRoute,
  faqIndexRoute,
  faqPageRoute,
  guideIndexRoute,
  guidePageRoute,
  privacyPolicyRoute,
  privacyPolicyEnRoute,
  termsOfUseRoute,
  termsOfUseEnRoute,
  createCompanyRoute,
  companyPublicRoute,
  companyStoreRoute,
  companyStoreProductRoute,
  companyFridgeRoute,
  fridgePosterRoute,
  fridgeQrPrintRoute,
  publicProfileRoute,
  adminLoginRoute,
  legacyLoginRoute,
  legacyFinishSignUpRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    immersive?: boolean;
  }
}
